import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
    DocumentIcon,
    PlayIcon,
    LinkIcon,
    LockClosedIcon,
    EyeIcon,
    CheckCircleIcon
} from '@heroicons/react/24/outline';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import 'videojs-contrib-quality-levels';
import 'videojs-hls-quality-selector';

const VideoPlayer = ({ src, onEnded, className, videoId }) => {
    const videoRef = useRef(null);
    const playerRef = useRef(null);

    // Create player only when src changes
    useEffect(() => {
        if (!videoRef.current) return;

        // Dispose existing player
        if (playerRef.current) {
            playerRef.current.dispose();
            playerRef.current = null;
        }

        const getStreamUrl = async () => {
            let streamUrl = src;

            if (src && src.endsWith('.mp4')) {
                try {
                    const filename = src.split('/').pop();
                    const response = await axios.get(`/api/video-hls/${filename}`);
                    streamUrl = response.data?.streamUrl || response.data || src;
                } catch (error) {
                    console.error('Error fetching HLS:', error);
                }
            }

            const player = videojs(videoRef.current, {
                controls: true,
                fluid: true,
                preload: 'none',
                playbackRates: [0.5, 1, 1.5, 2],
                html5: {
                    vhs: {
                        overrideNative: true
                    },
                    nativeVideoTracks: false,
                    nativeAudioTracks: false,
                    nativeTextTracks: false
                }
            });

            player.src({ src: streamUrl, type: 'application/x-mpegURL' });
            player.hlsQualitySelector({ displayCurrentQuality: true });

            player.ready(() => {
                player.currentTime(0);
            });

            player.on('loadedmetadata', () => {
                player.currentTime(0);
            });

            player.on('ended', onEnded);

            playerRef.current = player;
        };

        getStreamUrl();

        return () => {
            if (playerRef.current) {
                playerRef.current.dispose();
                playerRef.current = null;
            }
        };
    }, [src]); // Only depend on src

    // Update event listener when onEnded changes
    useEffect(() => {
        if (playerRef.current) {
            playerRef.current.off('ended');
            playerRef.current.on('ended', onEnded);
        }
    }, [onEnded]);

    return (
        <div className={className}>
            <div data-vjs-player>
                <video ref={videoRef} className="video-js vjs-big-play-centered" />
            </div>
        </div>
    );
};

const CourseContentViewer = ({
    courseId,
    materials,
    isEnrolled,
    canEdit,
    onEnroll,
    enrollmentLoading
}) => {
    const { user } = useAuth();
    const [selectedMaterial, setSelectedMaterial] = useState(null);
    const [progress, setProgress] = useState(null);

    const getIconForType = (type) => {
        switch (type) {
            case 'video': return PlayIcon;
            case 'link': return LinkIcon;
            default: return DocumentIcon;
        }
    };

    const canAccessMaterial = (material) => {
        return isEnrolled || canEdit || material.isFree;
    };

    // Auto-select first accessible video when materials change
    useEffect(() => {
        if (materials.length > 0) {
            const firstVideo = materials.find(m => m.type === 'video' && canAccessMaterial(m));
            if (firstVideo && (!selectedMaterial || !canAccessMaterial(selectedMaterial))) {
                setSelectedMaterial(firstVideo);
            }
        }
    }, [materials, isEnrolled, canEdit]);

    // Fetch progress for enrolled students
    useEffect(() => {
        if (isEnrolled && user?.role === 'student' && courseId) {
            fetchProgress();
        }
    }, [isEnrolled, user, courseId]);

    const fetchProgress = async () => {
        try {
            const response = await axios.get(`/api/course-progress/${courseId}`);
            setProgress(response.data);
        } catch (error) {
            console.error('Error fetching progress:', error);
        }
    };

    const updateMaterialProgress = async (materialId, isCompleted) => {
        if (!isEnrolled || user?.role !== 'student') return;

        try {
            await axios.put(`/api/course-progress/${courseId}/materials/${materialId}`, {
                isCompleted
            });
            await fetchProgress();
        } catch (error) {
            console.error('Error updating progress:', error);
            toast.error('Failed to update progress');
        }
    };

    const handleVideoEnd = useCallback(async () => {
        if (!selectedMaterial) return;

        // Mark current video as completed for enrolled students
        if (isEnrolled && user?.role === 'student') {
            await updateMaterialProgress(selectedMaterial._id, true);
        }

        // Find and play next video for all users
        const currentIndex = materials.findIndex(m => m._id === selectedMaterial._id);
        const nextVideo = materials.slice(currentIndex + 1).find(m =>
            m.type === 'video' && canAccessMaterial(m)
        );

        if (nextVideo) {
            setSelectedMaterial(nextVideo);
        }
    }, [selectedMaterial, isEnrolled, user?.role, materials, canAccessMaterial, updateMaterialProgress]);

    const checkIsMaterialCompleted = (materialId) => {
        if (!progress?.materialProgress) return false;
        const materialProgress = progress.materialProgress.find(p => p.materialId === materialId);
        return materialProgress?.isCompleted || false;
    };

    const freeMaterialsCount = materials.filter(m => m.isFree).length;
    const lockedMaterialsCount = materials.length - freeMaterialsCount;

    if (!materials || materials.length === 0) {
        return (
            <div className="card">
                <div className="text-center py-8">
                    <DocumentIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Materials Available</h3>
                    <p className="text-gray-600">
                        Course materials will appear here once the instructor uploads them.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="card">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-gray-900">Course Materials</h2>
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                        <span className="flex items-center">
                            <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                            {freeMaterialsCount} Free
                        </span>
                        {lockedMaterialsCount > 0 && (
                            <span className="flex items-center">
                                <LockClosedIcon className="h-3 w-3 mr-2" />
                                {lockedMaterialsCount} Premium
                            </span>
                        )}
                    </div>
                </div>

                {/* Progress Bar */}
                {isEnrolled && materials.length > 0 && (
                    <div className="mb-6">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-700">Course Progress</span>
                            <span className="text-sm text-gray-600">
                                {progress?.overallProgress?.toFixed(1) || 0}%
                            </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${progress?.overallProgress || 0}%` }}
                            ></div>
                        </div>
                    </div>
                )}

                {/* Main layout */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Video Player */}
                    <div className="md:col-span-2">
                        {selectedMaterial ? (
                            canAccessMaterial(selectedMaterial) ? (
                                selectedMaterial.type === 'video' ? (
                                    <VideoPlayer
                                        key={selectedMaterial._id}
                                        videoId={selectedMaterial._id}
                                        src={selectedMaterial.url}
                                        onEnded={handleVideoEnd}
                                        className="w-full rounded-lg shadow-lg"
                                    />
                                ) : (
                                    <div className="text-center p-6 border rounded-lg">
                                        <p className="text-gray-600 mb-4">
                                            {selectedMaterial.description || 'Click below to access this material'}
                                        </p>
                                        <a
                                            href={selectedMaterial.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn btn-primary flex items-center justify-center"
                                        >
                                            <EyeIcon className="h-4 w-4 mr-2" />
                                            Open Material
                                        </a>
                                    </div>
                                )
                            ) : (
                                <div className="p-6 text-center text-gray-500">
                                    <LockClosedIcon className="h-6 w-6 mx-auto mb-2" />
                                    <p>This material is locked. Enroll to access.</p>
                                </div>
                            )
                        ) : (
                            <div className="p-6 text-center text-gray-400 border rounded-lg">
                                Select a material from the right to view here
                            </div>
                        )}
                    </div>

                    {/* Materials List */}
                    <div className="md:col-span-1 border-l pl-4 space-y-3 md:max-h-screen md:overflow-auto">
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="font-semibold text-gray-800">All Materials</h3>
                            {isEnrolled && (
                                <span className="text-xs text-gray-500">Mark as completed</span>
                            )}
                        </div>
                        {materials.map((m, i) => {
                            const Icon = getIconForType(m.type);
                            const canAccess = canAccessMaterial(m);
                            const isCompleted = checkIsMaterialCompleted(m._id);

                            return (
                                <div
                                    key={m._id || i}
                                    className={`p-3 rounded-lg border transition ${selectedMaterial?._id === m._id
                                            ? 'bg-blue-50 border-blue-300'
                                            : 'bg-white border-gray-200 hover:bg-gray-50'
                                        }`}
                                >
                                    <div className="flex items-start space-x-3">
                                        {/* Checkbox for enrolled students */}
                                        {isEnrolled && (
                                            <div className="flex-shrink-0 mt-1">
                                                <input
                                                    type="checkbox"
                                                    checked={isCompleted}
                                                    onChange={(e) => updateMaterialProgress(m._id, e.target.checked)}
                                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                />
                                            </div>
                                        )}

                                        {/* Material content */}
                                        <div
                                            className={`flex items-center space-x-3 flex-1 cursor-pointer ${canAccess ? '' : 'opacity-50'
                                                }`}
                                            onClick={() => canAccess && setSelectedMaterial(m)}
                                        >
                                            {/* Icon */}
                                            {m.type === 'video' ? (
                                                <img
                                                    src="https://png.pngtree.com/png-vector/20190215/ourmid/pngtree-play-video-icon-graphic-design-template-vector-png-image_530837.jpg"
                                                    alt="Video"
                                                    className="w-12 h-8 rounded object-cover flex-shrink-0"
                                                />
                                            ) : (
                                                <Icon className={`h-5 w-5 flex-shrink-0 ${canAccess ? 'text-gray-600' : 'text-gray-400'}`} />
                                            )}

                                            {/* Material info */}
                                            <div className="flex-1 min-w-0">
                                                <p className={`font-medium text-sm truncate ${canAccess ? 'text-gray-800' : 'text-gray-400'
                                                    } ${selectedMaterial?._id === m._id ? 'text-blue-700' : ''}`}>
                                                    {m.title}
                                                </p>
                                                <div className="flex items-center space-x-2">
                                                    <span className="text-xs text-gray-500 capitalize">{m.type}</span>
                                                    {isCompleted && isEnrolled && (
                                                        <CheckCircleIcon className="h-3 w-3 text-green-600" />
                                                    )}
                                                </div>
                                                {!canAccess && (
                                                    <p className="text-xs text-red-500 flex items-center mt-1">
                                                        <LockClosedIcon className="h-3 w-3 mr-1" /> Locked
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Enrollment CTA */}
                {user?.role === 'student' && !isEnrolled && lockedMaterialsCount > 0 && (
                    <div className="mt-6 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold text-blue-900">
                                    Unlock Premium Content
                                </h3>
                                <p className="text-blue-700 mt-1">
                                    Enroll now to access all {materials.length} course materials, including {lockedMaterialsCount} premium {lockedMaterialsCount === 1 ? 'resource' : 'resources'}.
                                </p>
                            </div>
                            <button
                                onClick={onEnroll}
                                disabled={enrollmentLoading}
                                className="btn btn-primary px-6 py-3 text-lg disabled:opacity-50"
                            >
                                {enrollmentLoading ? 'Enrolling..' : 'Enroll Now'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CourseContentViewer;
